import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Center,
  Divider,
  VStack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useApiMutation,
  useApiQuery,
} from "../../../../utilities/apibelRequest";
import useToast from "../../../../utilities/useToast";
import { useAppState } from "../../../../components/App/AppProvider";
import Spinner from "../../../../components/chakra/common/Spinner";
import MentionEditor from "./TipTapMentionEditor";
import { ConfirmDeleteModal } from "./ConfirmDeleteModal";
import CommentCard from "./CommentCard";
import CommentsPagination from "./CommentsPagination";
import { Comment, Items } from "../../../../shared/_core/comments.types";

export interface ManualCommentsSectionProps {
  manualDetails: {
    manualID: string;
    responsibleUser: string;
  };
  items: Items[];
  onClick: (index: number) => void;
}
const ManualCommentsSection: React.FC<ManualCommentsSectionProps> = ({
  manualDetails,
  items,
  onClick,
}) => {
  const queryClient = useQueryClient();
  const { displayToast } = useToast();
  const { app } = useAppState();
  const editingEditorRef = useRef<any>(null);

  const [commentsPage, setCommentsPage] = useState(0);
  const [commentsPageSize] = useState(10);
  const [allComments, setAllComments] = useState<Comment[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(
    null,
  );
  const [editedContent, setEditedContent] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();

  // Queries and mutations
  const commentsQuery = useApiQuery("comments/findMany", {
    data: {
      manualID: [manualDetails.manualID],
      orderBy: { field: "createTs", dir: "desc" },
      limit: commentsPageSize,
      offset: commentsPage * commentsPageSize,
    },
  });

  const totalCommentsQuery = useApiQuery("comments/findMany", {
    data: { manualID: [manualDetails.manualID] },
  });

  const updateCommentMutation = useApiMutation("comments/update");
  const deleteCommentMutation = useApiMutation("comments/delete");

  // Computed values
  const commentsData = commentsQuery.data;
  const totalCommentsCount = totalCommentsQuery.data?.comments?.length || 0;
  const paginationCount = commentsData?.pagination?.count || 0;
  const totalPages =
    totalCommentsCount > 0
      ? Math.ceil(totalCommentsCount / commentsPageSize)
      : 0;
  const isCommentsUpdating =
    commentsQuery.isLoading ||
    commentsQuery.isFetching ||
    commentsQuery.isPending ||
    commentsQuery.isRefetching;

  // Effects
  useEffect(() => {
    if (commentsData && !commentsQuery.isLoading) {
      const { comments: newComments = [] } = commentsData;
      setAllComments(newComments);
      setHasNextPage(commentsPage + 1 < totalPages);
      setIsLoadingMore(false);
    }
  }, [commentsQuery.data, commentsQuery.isLoading, commentsPage, totalPages]);

  // Handlers
  const handleEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditedContent(comment.content);
  };

  const handleSaveComment = async (commentID: string) => {
    const editor = editingEditorRef.current;
    if (!editor) return;
    setIsSaving(true);
    const content = editor.getJSON();
    const isContentEmpty =
      !content?.content?.length ||
      (content.content.length === 1 &&
        content.content[0]?.type === "paragraph" &&
        !content.content[0]?.content?.length);

    if (isContentEmpty) {
      displayToast({
        title: "Empty Content",
        description: "Comment content cannot be empty.",
        status: "warning",
      });
      return;
    }

    try {
      await updateCommentMutation.mutateAsync({
        data: { commentID, content: JSON.stringify(content) },
      });
      setAllComments([]);
      setEditingCommentId(null);
      setEditedContent(null);
      displayToast({
        title: "Comment Updated",
        description: "Your comment has been updated successfully.",
        status: "success",
      });

      queryClient.invalidateQueries({ queryKey: ["comments"] });
    } catch {
      displayToast({
        title: "Update Failed",
        description: "Failed to update comment. Please try again.",
        status: "error",
      });
    }
    setIsSaving(false);
  };

  const handleDeleteComment = (commentID: string) => {
    setDeletingCommentId(commentID);
    onDeleteModalOpen();
  };

  const confirmDeleteComment = async () => {
    if (!deletingCommentId) return;

    try {
      await deleteCommentMutation.mutateAsync({ commentID: deletingCommentId });
      onDeleteModalClose();
      setDeletingCommentId(null);
      displayToast({
        title: "Comment Deleted",
        description: "Comment has been deleted successfully.",
        status: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["comments"] });
    } catch {
      displayToast({
        title: "Delete Failed",
        description: "Failed to delete comment. Please try again.",
        status: "error",
      });
    }
  };

  const handleSectionLinkClick = (sectionId: string) => {
    const sectionIndex = items.findIndex((item) => item.id === sectionId);
    if (sectionIndex !== -1) {
      onClick(sectionIndex);
    } else {
      document.getElementById(`section-${sectionId}`)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Pagination handlers
  const goToFirstPage = () => {
    if (commentsPage > 0 && !isLoadingMore) {
      setIsLoadingMore(true);
      setCommentsPage(0);
    }
  };

  const loadPreviousComments = () => {
    if (commentsPage > 0 && !isLoadingMore) {
      setIsLoadingMore(true);
      setCommentsPage((prev) => prev - 1);
    }
  };

  const loadMoreComments = () => {
    if (!isLoadingMore && hasNextPage) {
      setIsLoadingMore(true);
      setCommentsPage((prev) => prev + 1);
    }
  };

  const goToLastPage = () => {
    if (isLoadingMore) return;
    const lastPage = Math.max(
      0,
      Math.ceil(totalCommentsCount / commentsPageSize) - 1,
    );
    if (commentsPage !== lastPage) {
      setIsLoadingMore(true);
      setCommentsPage(lastPage);
    }
  };

  return (
    <>
      <VStack
        align="flex-start"
        spacing={4}
        w="full"
        height="calc(100vh - 420px)">
        <Divider />

        <VStack
          align="flex-start"
          spacing={4}
          w="full"
          flex="1"
          overflowY="auto"
          p={2}>
          {isCommentsUpdating ? (
            <Box pos="absolute" left="50%" transform="translate(-50%)">
              <Spinner size="lg" />
            </Box>
          ) : allComments.length === 0 ? (
            <Center w="full" py={8}>
              <VStack spacing={2}>
                <Text color="gray.400" fontSize="sm">
                  No comments yet.
                </Text>
                <Text color="gray.400" fontSize="sm">
                  Be the first to add one!
                </Text>
              </VStack>
            </Center>
          ) : (
            allComments.map((comment) => {
              const isCommentAuthor = app.userInfo.userID === comment.userID;
              const isEditingComment = editingCommentId === comment.id;
              const canDeleteComment =
                app.permissions.NOTES.DELETE.ALL ||
                (app.permissions.NOTES.DELETE.SELF && isCommentAuthor);
              const canEditComment = isCommentAuthor;

              return (
                <CommentCard
                  key={comment.id}
                  comment={comment}
                  isEditing={isEditingComment}
                  editedContent={editedContent}
                  isCommentAuthor={isCommentAuthor}
                  canEditComment={canEditComment}
                  canDeleteComment={canDeleteComment}
                  isCommentsUpdating={isCommentsUpdating}
                  editorRef={editingEditorRef}
                  onEdit={handleEditComment}
                  onDelete={handleDeleteComment}
                  onSave={handleSaveComment}
                  onCancel={() => {
                    setEditingCommentId(null);
                    setEditedContent(null);
                  }}
                  onContentChange={setEditedContent}
                  onSectionLinkClick={handleSectionLinkClick}
                  isSaving={isSaving}
                />
              );
            })
          )}
        </VStack>

        <Box w="full">
          <MentionEditor />
        </Box>

        <CommentsPagination
          currentPage={commentsPage}
          totalPages={totalPages}
          hasNextPage={hasNextPage}
          paginationCount={paginationCount}
          isLoading={isCommentsUpdating}
          onFirstPage={goToFirstPage}
          onPreviousPage={loadPreviousComments}
          onNextPage={loadMoreComments}
          onLastPage={goToLastPage}
        />
      </VStack>

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        onDelete={confirmDeleteComment}
        deleteType="comment"
        isLoading={deleteCommentMutation.isPending}
      />
    </>
  );
};

export default ManualCommentsSection;
